import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import BrandLogo from '../components/BrandLogo';
import Icon from '../components/Icon';
import { ALLOWED_ROLES, getUsers, searchOrders, getComplaints } from '../api';
import '../css/dashboard.css';

// Стартовая страница админки: приветствие, живые счётчики и быстрый переход в разделы.

const EASE = [0.22, 1, 0.36, 1];
const STAFF_ROLES = [ALLOWED_ROLES.SUPPORT, ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stagger = (delay = 0.06) => ({
  hidden: {},
  show: { transition: { staggerChildren: delay } },
});

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

const greetingKey = (hour) => {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 18) return 'day';
  if (hour < 23) return 'evening';
  return 'night';
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// Плавный счётчик от 0 до value (easeOutCubic), без анимации при reduced motion.
const useCountUp = (value, duration = 1100) => {
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (value == null) return undefined;
    if (reduceMotion) {
      setDisplay(value);
      return undefined;
    }
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setDisplay(Math.round(value * (1 - (1 - p) ** 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value, duration, reduceMotion]);

  return display;
};

// Мягкое пятно света под курсором — только CSS-переменные, без ре-рендеров.
const trackSpotlight = (event) => {
  const el = event.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${event.clientX - rect.left}px`);
  el.style.setProperty('--my', `${event.clientY - rect.top}px`);
};

const StatCard = ({ icon, label, hint, state, locale, unavailable, onClick }) => {
  const count = useCountUp(state.value);
  return (
    <motion.button type="button" variants={fadeUp} className="home-stat" onClick={onClick} onMouseMove={trackSpotlight}>
      <span className="home-stat-icon">
        <Icon name={icon} size={18} />
      </span>
      <span className="home-stat-value">
        {state.loading ? (
          <span className="home-skeleton" aria-hidden />
        ) : state.value == null ? (
          <span className="home-stat-na">{unavailable}</span>
        ) : (
          count.toLocaleString(locale)
        )}
      </span>
      <span className="home-stat-label">{label}</span>
      <span className="home-stat-hint">{hint}</span>
    </motion.button>
  );
};

const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const user = useMemo(readUser, []);
  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'ro' ? 'ro-RO' : 'en-US';

  const [stats, setStats] = useState({
    users: { loading: true, value: null },
    newUsers: { loading: true, value: null },
    orders: { loading: true, value: null },
    complaints: { loading: true, value: null },
  });
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const requests = {
      users: () => getUsers(0, 1, 'createDate'),
      newUsers: () => getUsers(0, 1, 'createDate', '', '', '', '', 'DESC', startOfToday()),
      orders: () => searchOrders({ page: 0, size: 1 }),
      complaints: () => getComplaints(0, 1, ['PENDING', 'IN_REVIEW']),
    };
    Object.entries(requests).forEach(([key, request]) => {
      request()
        .then((data) => {
          const total = Number(data?.totalElements);
          return Number.isFinite(total) ? total : null;
        })
        .catch(() => null)
        .then((value) => {
          if (mounted.current) setStats((prev) => ({ ...prev, [key]: { loading: false, value } }));
        });
    });
    return () => {
      mounted.current = false;
    };
  }, []);

  const firstName = (user?.fullName || '').trim().split(/\s+/)[0];
  const greeting = t(`homepage.greeting.${greetingKey(new Date().getHours())}`);
  const todayRaw = new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  const today = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);

  const statCards = [
    { key: 'users', icon: 'users', path: '/users' },
    { key: 'newUsers', icon: 'user-plus', path: '/users' },
    { key: 'orders', icon: 'orders', path: '/orders' },
    { key: 'complaints', icon: 'alert-triangle', path: '/complaints' },
  ];

  const sections = [
    { path: '/orders', icon: 'orders', title: t('nav.orders'), text: t('homepage.sections.orders'), roles: STAFF_ROLES },
    { path: '/users', icon: 'users', title: t('nav.users'), text: t('homepage.sections.users'), roles: STAFF_ROLES },
    { path: '/complaints', icon: 'complaints', title: t('nav.complaints'), text: t('homepage.sections.complaints'), roles: STAFF_ROLES },
    { path: '/chat', icon: 'message-square', title: t('nav.chat', { defaultValue: 'Чат' }), text: t('homepage.sections.chat'), roles: STAFF_ROLES },
    { path: '/calendar', icon: 'calendar', title: t('nav.calendar', { defaultValue: 'Календарь' }), text: t('homepage.sections.calendar'), roles: STAFF_ROLES },
    { path: '/logs', icon: 'database', title: t('nav.logs', { defaultValue: 'Логи' }), text: t('homepage.sections.logs'), roles: STAFF_ROLES },
    {
      path: '/statistics',
      icon: 'pie-chart',
      title: t('nav.statistics', { defaultValue: 'Статистика' }),
      text: t('homepage.sections.statistics'),
      roles: [ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV],
    },
    {
      path: '/admin',
      icon: 'settings',
      title: t('nav.admin'),
      text: t('homepage.sections.admin'),
      roles: [ALLOWED_ROLES.MODERATOR, ALLOWED_ROLES.ADMIN, ALLOWED_ROLES.DEV],
    },
  ].filter((s) => s.roles.includes(user?.role));

  const tips = [
    { icon: 'search', key: 'search' },
    { icon: 'mail', key: 'export' },
    { icon: 'theme-dark', key: 'theme' },
    { icon: 'globe', key: 'language' },
  ];

  const inView = reduceMotion
    ? { initial: 'show', animate: 'show' }
    : { initial: 'hidden', whileInView: 'show', viewport: { once: true, amount: 0.2 } };

  return (
    <div className="home-page">
      {/* HERO */}
      <section className="home-hero">
        <div className="home-hero-bg" aria-hidden>
          <span className="home-hero-grid" />
          <span className="home-hero-glow home-hero-glow--a" />
          <span className="home-hero-glow home-hero-glow--b" />
        </div>

        <motion.div className="home-hero-inner" variants={stagger(0.08)} initial={reduceMotion ? 'show' : 'hidden'} animate="show">
          <motion.div variants={fadeUp} className="home-pill">
            <BrandLogo className="home-pill-logo" />
            <span>{t('homepage.badge')}</span>
            <span className="home-pill-sep" />
            <span className="home-pill-muted">{today}</span>
          </motion.div>

          <motion.p variants={fadeUp} className="home-greeting">
            {firstName ? `${greeting}, ${firstName}` : greeting}
            <span className="home-wave" aria-hidden>👋</span>
          </motion.p>

          <motion.h1 variants={fadeUp} className="home-title">
            {t('homepage.titleLead')} <span className="home-title-accent">{t('homepage.titleAccent')}</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="home-lead">
            {t('homepage.lead')}
          </motion.p>

          <motion.div variants={fadeUp} className="home-cta">
            <button type="button" className="home-btn home-btn--primary" onClick={() => navigate('/orders')}>
              {t('homepage.ctaPrimary')}
              <Icon name="arrow-right" size={16} />
            </button>
            <button type="button" className="home-btn home-btn--ghost" onClick={() => navigate('/users')}>
              <Icon name="search" size={16} />
              {t('homepage.ctaSecondary')}
            </button>
          </motion.div>

          {user?.role && (
            <motion.div variants={fadeUp} className="home-role">
              <Icon name="shield" size={14} />
              {t('homepage.role')}: <b>{t(`users.roles.${user.role}`, { defaultValue: user.role })}</b>
            </motion.div>
          )}
        </motion.div>
      </section>

      {/* STATS */}
      <motion.section className="home-stats-band" variants={stagger(0.07)} {...inView}>
        {statCards.map((card) => (
          <StatCard
            key={card.key}
            icon={card.icon}
            label={t(`homepage.stats.${card.key}`)}
            hint={t(`homepage.stats.${card.key}Hint`)}
            state={stats[card.key]}
            locale={locale}
            unavailable={t('homepage.stats.unavailable')}
            onClick={() => navigate(card.path)}
          />
        ))}
      </motion.section>

      {/* SECTIONS */}
      <section className="home-section">
        <motion.header className="home-section-head" variants={stagger()} {...inView}>
          <motion.span variants={fadeUp} className="home-tag">{t('homepage.sections.badge')}</motion.span>
          <motion.h2 variants={fadeUp} className="home-h2">{t('homepage.sections.title')}</motion.h2>
          <motion.p variants={fadeUp} className="home-sub">{t('homepage.sections.subtitle')}</motion.p>
        </motion.header>

        <motion.div className="home-grid" variants={stagger(0.05)} {...inView}>
          {sections.map((card) => (
            <motion.button
              key={card.path}
              type="button"
              variants={fadeUp}
              className="home-card"
              onClick={() => navigate(card.path)}
              onMouseMove={trackSpotlight}
            >
              <span className="home-card-icon">
                <Icon name={card.icon} size={20} />
              </span>
              <span className="home-card-title">{card.title}</span>
              <span className="home-card-text">{card.text}</span>
              <span className="home-card-go">
                {t('homepage.sections.open')}
                <Icon name="arrow-right" size={14} />
              </span>
            </motion.button>
          ))}
        </motion.div>
      </section>

      {/* TIPS */}
      <motion.section className="home-tips" variants={stagger(0.07)} {...inView}>
        <motion.div variants={fadeUp} className="home-tips-intro">
          <span className="home-tag">{t('homepage.tips.badge')}</span>
          <h2 className="home-h2">{t('homepage.tips.title')}</h2>
          <p className="home-sub">{t('homepage.tips.subtitle')}</p>
        </motion.div>
        <div className="home-tips-list">
          {tips.map((tip) => (
            <motion.div key={tip.key} variants={fadeUp} className="home-tip">
              <span className="home-tip-icon">
                <Icon name={tip.icon} size={18} />
              </span>
              <div>
                <div className="home-tip-title">{t(`homepage.tips.${tip.key}Title`)}</div>
                <div className="home-tip-text">{t(`homepage.tips.${tip.key}Text`)}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>
    </div>
  );
};

export default Dashboard;
